import { useState, useEffect, useRef, useCallback } from 'react'
import { trpc } from '@/lib/api/trpc'

export type EmailStatus = 'idle' | 'checking' | 'available' | 'taken' | 'disposable' | 'invalid'

const DISPOSABLE_DOMAINS = new Set([
	'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email',
	'yopmail.com', 'sharklasers.com', 'guerrillamailblock.com', 'grr.la',
	'guerrillamail.info', 'guerrillamail.net', 'guerrillamail.org',
	'guerrillamail.de', 'temp-mail.org', 'tempail.com', 'dispostable.com',
	'trashmail.com', 'trashmail.me', 'trashmail.net', 'mailnesia.com',
	'maildrop.cc', 'discard.email', 'mailcatch.com', 'fakeinbox.com',
	'getnada.com', 'emailondeck.com', 'tempinbox.com', 'binkmail.com',
	'safetymail.info', 'filzmail.com', 'trashymail.com', 'mailexpire.com',
	'tempmailo.com', 'mohmal.com', 'burpcollaborator.net', 'trbvn.com',
	'10minutemail.com', 'minutemail.com', 'emailfake.com', 'crazymailing.com',
	'harakirimail.com', 'jetable.org', 'get2mail.fr', 'meltmail.com',
	'spaml.com', 'mailnull.com', 'spamgourmet.com', 'mytemp.email',
	'tempr.email', 'temp-mail.io', 'mohmal.im', 'emailnator.com',
])

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const getDomain = (email: string): string => email.split('@')[1]?.toLowerCase() ?? ''

const isDisposable = (email: string): boolean => DISPOSABLE_DOMAINS.has(getDomain(email))

const isValidFormat = (email: string): boolean => EMAIL_RE.test(email)

/**
 * Debounced email validation hook.
 * Checks format, disposable domain blocklist, and Clerk availability.
 */
export const useEmailValidation = (debounceMs = 500) => {
	const [status, setStatus] = useState<EmailStatus>('idle')
	const [message, setMessage] = useState('')
	const timerRef = useRef<ReturnType<typeof setTimeout>>()
	const abortRef = useRef<AbortController>()
	const trpcUtils = trpc.useUtils()

	const checkAvailability = useCallback(
		async (email: string) => {
			abortRef.current?.abort()
			const controller = new AbortController()
			abortRef.current = controller

			try {
				const { available } = await trpcUtils.signupProfile.checkEmailAvailability.fetch(
					{ email },
				)

				if (controller.signal.aborted) return

				setStatus(available ? 'available' : 'taken')
				setMessage(available ? '' : 'This email is already taken')
			} catch {
				if (controller.signal.aborted) return
				setStatus('available')
				setMessage('')
			}
		},
		[trpcUtils],
	)

	const validate = useCallback(
		(email: string) => {
			clearTimeout(timerRef.current)
			abortRef.current?.abort()

			if (!email.trim()) {
				setStatus('idle')
				setMessage('')
				return
			}

			if (!isValidFormat(email)) {
				setStatus('invalid')
				setMessage('Please enter a valid email address')
				return
			}

			if (isDisposable(email)) {
				setStatus('disposable')
				setMessage('Disposable email addresses are not allowed')
				return
			}

			setStatus('checking')
			setMessage('')

			timerRef.current = setTimeout(() => {
				checkAvailability(email)
			}, debounceMs)
		},
		[debounceMs, checkAvailability],
	)

	useEffect(() => () => {
		clearTimeout(timerRef.current)
		abortRef.current?.abort()
	}, [])

	return { status, message, validate } as const
}
